import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './hooks/useAuth'
import ScrollToTop from './components/ScrollToTop'
import UpdateBanner from './components/UpdateBanner'
import Onboarding from './components/Onboarding'
import PWAInstallBanner from './components/PWAInstallBanner'
import { isNativeApp } from './lib/native'

// Pages secondaires lazy load (40 routes → ~3-15KB chacune)
const Mentions = lazy(() => import('./pages/Mentions'))
const Confidentialite = lazy(() => import('./pages/Confidentialite'))
const Contact = lazy(() => import('./pages/Contact'))
const Login = lazy(() => import('./pages/Login'))
const Verify = lazy(() => import('./pages/Verify'))
const AppHome = lazy(() => import('./pages/AppHome'))
const CreateEvent = lazy(() => import('./pages/CreateEvent'))
const EventDetail = lazy(() => import('./pages/EventDetail'))
const PublicEvent = lazy(() => import('./pages/PublicEvent'))
const PublicPost = lazy(() => import('./pages/PublicPost'))
const Agenda = lazy(() => import('./pages/Agenda'))
const Bouge = lazy(() => import('./pages/Bouge'))
const Plus = lazy(() => import('./pages/Plus'))
const Profil = lazy(() => import('./pages/Profil'))
const Parametres = lazy(() => import('./pages/Parametres'))
const Notifications = lazy(() => import('./pages/Notifications'))
const MemberPublic = lazy(() => import('./pages/MemberPublic'))
const EditEvent = lazy(() => import('./pages/EditEvent'))
const Annuaire = lazy(() => import('./pages/Annuaire'))
const Contacts = lazy(() => import('./pages/Contacts'))
const Polls = lazy(() => import('./pages/Polls'))
const PublicPoll = lazy(() => import('./pages/PublicPoll'))
const Pools = lazy(() => import('./pages/Pools'))
const PublicPool = lazy(() => import('./pages/PublicPool'))
const Wishlists = lazy(() => import('./pages/Wishlists'))
const WishlistAdmin = lazy(() => import('./pages/WishlistAdmin'))
const PublicWishlist = lazy(() => import('./pages/PublicWishlist'))
const PollAdmin = lazy(() => import('./pages/PollAdmin'))
const Ladilafe = lazy(() => import('./pages/Ladilafe'))
const Messages = lazy(() => import('./pages/Messages'))
const Conversation = lazy(() => import('./pages/Conversation'))
const RevuePresse = lazy(() => import('./pages/RevuePresse'))
const Annonces = lazy(() => import('./pages/Annonces'))
const Sorties = lazy(() => import('./pages/Sorties'))
const OutingDetail = lazy(() => import('./pages/OutingDetail'))
const Bons = lazy(() => import('./pages/Bons'))
const Modules = lazy(() => import('./pages/Modules'))
const ModuleDetail = lazy(() => import('./pages/ModuleDetail'))
const Parrainage = lazy(() => import('./pages/Parrainage'))
const AdminModeration = lazy(() => import('./pages/AdminModeration'))
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'))
const Suspended = lazy(() => import('./pages/Suspended'))
const InviteLanding = lazy(() => import('./pages/InviteLanding'))
const CalendrierFetes = lazy(() => import('./pages/CalendrierFetes'))
const Welcome = lazy(() => import('./pages/Welcome'))

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center text-nuit/60">Chargement...</div>
)

// app.mondalon.net = app SPA (pas de vitrine). La racine renvoie directement
// vers l'espace si connecté, sinon vers la connexion. La vitrine = mondalon.net (Astro).
function RootRedirect() {
  const { user, loading } = useAuth()
  if (loading) return <PageLoader />
  return <Navigate to={user ? '/app' : '/login'} replace />
}

export default function App() {
  // Capte la provenance d'acquisition (UTM) dès l'arrivée sur le site (n'importe quelle page :
  // pub → /tarifs?utm_source=...). Persistée en localStorage (last-touch), lue au signup
  // par Login.tsx et propagée au compte. Même logique que le referralCode.
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search)
      const source = p.get('utm_source')
      const medium = p.get('utm_medium')
      const campaign = p.get('utm_campaign')
      if (source || medium || campaign) {
        localStorage.setItem(
          'mondalon.utm',
          JSON.stringify({ source: source || undefined, medium: medium || undefined, campaign: campaign || undefined })
        )
      }
    } catch { /* localStorage indispo (mode privé) — non bloquant */ }
  }, [])

  return (
    <AuthProvider>
      <ScrollToTop />
      {/* Pas de SW / bannière de MAJ en natif (reader-app : évite un SW cachant une version périmée) */}
      {!isNativeApp() && <UpdateBanner />}
      <Onboarding />
      <PWAInstallBanner />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/contact" element={<Contact />} />
          {/* 🔴 2026-09-12 — LA PAGE D'ABONNEMENT N'EXISTE PLUS DANS L'APPLICATION.
              Elle vit sur la vitrine (`mondalon.net/tarifs`, île `Abonnement.tsx`), qui porte
              désormais le paiement complet. `/app/abonnement` est redirigé par nginx vers la
              vitrine, sans condition (web comme natif) : il n'y a plus rien à servir ici.
              Motif, et il vaut pour tout le parc : une surface marchande présente dans l'app,
              même masquée, est ce qu'Apple sanctionne en 5.6 — grief visant le COMPTE
              développeur. Voir `src/lib/abonnement.ts` et `sysop/docs/REGLE-SAAS-VITRINE-APP.md`.
              ⚠️ Ne pas « remettre la route pour le web » : le web a la vitrine. */}
          <Route path="/mentions-legales" element={<Mentions />} />
          <Route path="/confidentialite" element={<Confidentialite />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Login />} />
          <Route path="/register" element={<Login />} />
          <Route path="/inscription" element={<Login />} />
          <Route path="/verify" element={<Verify />} />
          <Route path="/app/bienvenue" element={<Welcome />} />
          <Route path="/app" element={<AppHome />} />
          <Route path="/app/agenda" element={<Agenda />} />
          <Route path="/app/bouge" element={<Bouge />} />
          <Route path="/app/plus" element={<Plus />} />
          <Route path="/app/create" element={<CreateEvent />} />
          <Route path="/app/events/:id" element={<EventDetail />} />
          <Route path="/app/events/:id/edit" element={<EditEvent />} />
          <Route path="/app/profil" element={<Profil />} />
          <Route path="/app/parametres" element={<Parametres />} />
          <Route path="/app/parametres/notifications" element={<Notifications />} />
          <Route path="/app/contacts" element={<Contacts />} />
          <Route path="/app/sondages" element={<Polls />} />
          <Route path="/app/sondages/:slug" element={<PollAdmin />} />
          <Route path="/app/cagnottes" element={<Pools />} />
          <Route path="/app/ladilafe" element={<Ladilafe />} />
          <Route path="/app/revue-presse" element={<RevuePresse />} />
          <Route path="/app/annonces" element={<Annonces />} />
          <Route path="/app/sorties" element={<Sorties />} />
          <Route path="/app/sorties/:id" element={<OutingDetail />} />
          <Route path="/app/bons" element={<Bons />} />
          <Route path="/app/messages" element={<Messages />} />
          <Route path="/app/messages/:id" element={<Conversation />} />
          <Route path="/app/listes-cadeaux" element={<Wishlists />} />
          <Route path="/app/listes-cadeaux/:slug" element={<WishlistAdmin />} />
          <Route path="/p/:slug" element={<PublicPoll />} />
          <Route path="/pot/:slug" element={<PublicPool />} />
          <Route path="/cadeaux/:slug" element={<PublicWishlist />} />
          <Route path="/annuaire" element={<Annuaire />} />
          <Route path="/modules" element={<Modules />} />
          <Route path="/modules/:slug" element={<ModuleDetail />} />
          <Route path="/invite/:code" element={<InviteLanding />} />
          <Route path="/app/parrainage" element={<Parrainage />} />
          <Route path="/app/admin" element={<AdminDashboard />} />
          <Route path="/app/admin/moderation" element={<AdminModeration />} />
          <Route path="/suspended" element={<Suspended />} />
          <Route path="/fetes-974" element={<CalendrierFetes />} />
          <Route path="/e/:slug" element={<PublicEvent />} />
          <Route path="/post/:slug" element={<PublicPost />} />
          <Route path="/m/:id" element={<MemberPublic />} />
          {/* Catch-all : évite l'écran blanc sur URL inconnue (RootRedirect route vers /app ou /login) */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  )
}
